import React, {
	ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

import { CenteredNavContext } from "@components/centered-nav/CenteredNavContext";
import { scrollChildIntoView } from "@components/centered-nav/utils";
import "swiper/css";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";

import { SwiperContext } from "./SwiperContext";
import { SwiperType } from "./SwiperType";
import { swiperConfiguration } from "./swiperConfiguration";
import { useSwiper } from "./useSwiper";

export function SwiperWrapper({
	children,
	type = SwiperType.SHOW_FIRST,
	className,
	customSpacing = 12,
	at,
	noSpacingAfterLastItem,
}: {
	children: ReactNode;
	type: SwiperType;
	className?: string;
	at?: string;
	customSpacing?: number;
	noSpacingAfterLastItem?: boolean;
}) {
	const [swiperState, setSwiperState] = useState<SwiperClass | null>(null);
	const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);

	const [activeTabI, setActiveTabI] = useState<number | null>(null);
	const navRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (type === SwiperType.CENTERED && activeTabI !== null) {
			const timer = setTimeout(() => {
				if (navRef.current) {
					scrollChildIntoView(navRef.current, activeTabI);
				}
			}, 0);

			return () => clearTimeout(timer);
		}
	}, [activeTabI, type]);

	useEffect(() => {
		if (type === SwiperType.SHOW_FIRST) {
			const timer = setTimeout(() => {
				swiperSlideToIndex(swiperState);
			}, 0);

			return () => clearTimeout(timer);
		}
	}, [selectedSlideIndex, type]);

	const getChildren = () => {
		let childArray = React.Children.toArray(children);

		if (type === SwiperType.CENTERED) {
			return childArray;
		}

		if (type === SwiperType.SHOW_FIRST) {
			childArray = childArray.map((child, index) => {
				const isActive =
					React.isValidElement(child) &&
					typeof child.props.isActive === "boolean"
						? child.props.isActive
						: selectedSlideIndex === index;

				const lastItem = index === childArray.length - 1;

				return (
					<SwiperSlide key={index} style={{ width: "auto" }}>
						<SwiperWrapper.SwiperItem
							tabIndex={index}
							isActive={isActive}
						>
							{child}
							{lastItem && !noSpacingAfterLastItem && (
								<div style={{ width: `${customSpacing}px` }} />
							)}
						</SwiperWrapper.SwiperItem>
					</SwiperSlide>
				);
			});
		}

		return childArray;
	};

	const swiperSlideToIndex = (swiper: SwiperClass | null) => {
		if (!swiper) return;

		const selectedEl = swiper.slides[selectedSlideIndex];
		const prevEl = swiper.slides[selectedSlideIndex - 1];

		const widthNeeded =
			selectedEl?.offsetWidth +
			(prevEl?.offsetWidth || 0) +
			customSpacing;

		if (swiper.slides.length - 1 === selectedSlideIndex) {
			swiper.slideTo(selectedSlideIndex);
			return;
		}

		if (swiper.width > widthNeeded) {
			swiper.slideTo(selectedSlideIndex - 1);
		} else {
			swiper.slideTo(selectedSlideIndex);
		}
	};

	if (type === SwiperType.CENTERED) {
		return (
			<CenteredNavContext.Provider value={{ activeTabI, setActiveTabI }}>
				<nav ref={navRef} className={className ?? ""} data-at={at}>
					{getChildren()}
				</nav>
			</CenteredNavContext.Provider>
		);
	}

	return (
		<SwiperContext.Provider value={{ swiperState, setSelectedSlideIndex }}>
			<Swiper
				{...{
					...swiperConfiguration,
					spaceBetween: customSpacing,
				}}
				onSwiper={(swiper) => {
					swiper.enable();
					setSwiperState(swiper);
				}}
				onResize={(swiper) => {
					swiper.enable();
					swiperSlideToIndex(swiper);
				}}
				onUpdate={(swiper) => {
					swiperSlideToIndex(swiper);
				}}
				className={className ?? ""}
			>
				{getChildren()}
			</Swiper>
		</SwiperContext.Provider>
	);
}

//SliderItem or CenteredNavItem
SwiperWrapper.SliderItem = function ({
	isActive,
	tabIndex,
	children,
}: {
	isActive: boolean;
	tabIndex: number;
	children: ReactNode;
}) {
	const { setActiveTabI } = useContext(CenteredNavContext);

	useEffect(() => {
		if (isActive) {
			setActiveTabI(tabIndex);
		}
	}, [isActive, tabIndex, setActiveTabI]);

	return <>{children}</>;
};

SwiperWrapper.SwiperItem = function ({
	children,
	tabIndex,
	isActive,
	secondaryName,
}: {
	children: ReactNode;
	tabIndex: number;
	isActive: boolean;
	secondaryName?: string | null;
}) {
	useSwiper({ tabIndex, isActive, secondaryName: secondaryName ?? null });

	return <>{children}</>;
};
